import { z } from "zod";
import { BusinessDate, Instant, Version } from "./common.js";
import { Id } from "./ids.js";

/**
 * Account level in Iranian accounting hierarchy:
 * گروه (group) → کل (general) → معین (subsidiary) → تفصیلی (detail).
 */
export const AccountLevel = z.enum(["group", "general", "subsidiary", "detail"]);
export type AccountLevel = z.infer<typeof AccountLevel>;

/**
 * Normal account nature:
 * - debit (بدهکار): Assets, Cost of sales, Expenses
 * - credit (بستانکار): Liabilities, Equity, Revenue
 */
export const AccountNature = z.enum(["debit", "credit"]);
export type AccountNature = z.infer<typeof AccountNature>;

export const AccountStatus = z.enum(["active", "archived"]);
export type AccountStatus = z.infer<typeof AccountStatus>;

export const Account = z.object({
  id: Id,
  version: Version.optional(),
  code: z.string().min(1),
  name: z.string().min(1),
  level: AccountLevel,
  nature: AccountNature,
  parentId: Id.nullable().optional(),
  status: AccountStatus.default("active"),
  role: z.string().nullable().optional(),
});
export type Account = z.infer<typeof Account>;

export const AccountCreateInput = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  level: AccountLevel,
  nature: AccountNature,
  parentId: Id.nullable().optional(),
  role: z.string().nullable().optional(),
});
export type AccountCreateInput = z.infer<typeof AccountCreateInput>;

export const AccountUpdateInput = z.object({
  name: z.string().min(1).optional(),
  role: z.string().nullable().optional(),
});
export type AccountUpdateInput = z.infer<typeof AccountUpdateInput>;

export const FloatingDetail = z.object({
  id: Id,
  organizationId: Id,
  unitId: Id.nullable().optional(),
  code: z.string().optional(),
  name: z.string().min(1),
});
export type FloatingDetail = z.infer<typeof FloatingDetail>;

export const AccountingVoucherLine = z.object({
  id: Id,
  accountId: Id,
  debitRial: z.number().int().nonnegative(),
  creditRial: z.number().int().nonnegative(),
  description: z.string().optional(),
  detailId: Id.nullable().optional(),
  detailType: z.string().nullable().optional(),
});
export type AccountingVoucherLine = z.infer<typeof AccountingVoucherLine>;

export const AccountingVoucherLineInput = z.object({
  accountId: Id,
  debitRial: z.number().int().nonnegative(),
  creditRial: z.number().int().nonnegative(),
  description: z.string().optional(),
  detailId: Id.nullable().optional(),
  detailType: z.string().nullable().optional(),
});
export type AccountingVoucherLineInput = z.infer<typeof AccountingVoucherLineInput>;

export const AccountingVoucherStatus = z.enum(["draft", "posted"]);
export type AccountingVoucherStatus = z.infer<typeof AccountingVoucherStatus>;

export const AccountingVoucher = z.object({
  id: Id,
  version: Version.optional(),
  fiscalYear: z.number().int().positive(),
  voucherNumber: z.number().int().positive().nullable().optional(),
  date: BusinessDate,
  description: z.string().min(1),
  status: AccountingVoucherStatus.default("draft"),
  lines: z.array(AccountingVoucherLine).min(2),
  postedAt: Instant.nullable().optional(),
  postedBy: Id.nullable().optional(),
  sourceType: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
  reversedVoucherId: Id.nullable().optional(),
  isReversal: z.boolean().optional(),
});
export type AccountingVoucher = z.infer<typeof AccountingVoucher>;

export const AccountingVoucherCreateInput = z.object({
  date: BusinessDate,
  description: z.string().min(1),
  status: AccountingVoucherStatus.default("draft"),
  lines: z.array(AccountingVoucherLineInput).min(2),
  sourceType: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
});
export type AccountingVoucherCreateInput = z.infer<typeof AccountingVoucherCreateInput>;

export const AccountingVoucherUpdateInput = z.object({
  date: BusinessDate.optional(),
  description: z.string().min(1).optional(),
  lines: z.array(AccountingVoucherLineInput).min(2).optional(),
});
export type AccountingVoucherUpdateInput = z.infer<typeof AccountingVoucherUpdateInput>;

export const AccountingVoucherReverseInput = z.object({
  date: BusinessDate,
  reason: z.string().min(1),
});
export type AccountingVoucherReverseInput = z.infer<typeof AccountingVoucherReverseInput>;

// ==========================================
// ACCOUNT ROLES (AG-37)
// ==========================================

export const AccountRole = z.enum([
  "receivable",
  "revenue",
  "cash",
  "bank",
  "cheques-in-hand",
  "vat-payable",
  // Purchasing (lead, 2026-11-03): supplier bills and payments.
  "payable",
  "purchases",
  "cheques-payable",
  // Year-end closing (AG-43)
  "year-result",
]);
export type AccountRole = z.infer<typeof AccountRole>;

export const AccountRoleMapping = z.object({
  role: AccountRole,
  accountId: Id,
});
export type AccountRoleMapping = z.infer<typeof AccountRoleMapping>;

export const AccountRoleMappingView = z.object({
  role: AccountRole,
  accountId: Id,
  accountCode: z.string().optional(),
  accountName: z.string().optional(),
});
export type AccountRoleMappingView = z.infer<typeof AccountRoleMappingView>;

export const SetAccountRoleInput = z.object({
  role: AccountRole,
  accountId: Id,
});
export type SetAccountRoleInput = z.infer<typeof SetAccountRoleInput>;

// ==========================================
// RETRY QUEUE & BACKFILL (AG-37)
// ==========================================

export const AccountingRetryStatus = z.enum(["pending", "resolved", "failed"]);
export type AccountingRetryStatus = z.infer<typeof AccountingRetryStatus>;

export const AccountingRetryEvent = z.object({
  id: Id,
  createdAt: Instant,
  eventType: z.string(),
  sourceType: z.string(),
  sourceId: z.string(),
  payload: z.record(z.string(), z.unknown()),
  errorMessage: z.string(),
  status: AccountingRetryStatus,
  attempts: z.number().int().nonnegative(),
  lastAttemptAt: Instant,
  resolvedAt: Instant.nullable().optional(),
  voucherId: Id.nullable().optional(),
});
export type AccountingRetryEvent = z.infer<typeof AccountingRetryEvent>;

export const AccountingBackfillResult = z.object({
  invoicesCount: z.number().int().nonnegative(),
  notesCount: z.number().int().nonnegative(),
  paymentsCount: z.number().int().nonnegative(),
  vouchersPosted: z.number().int().nonnegative(),
  vouchersSkipped: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
});
export type AccountingBackfillResult = z.infer<typeof AccountingBackfillResult>;

// ==========================================
// REPORTS (AG-40)
// ==========================================

export const AccountingJournalLine = z.object({
  id: Id,
  lineNumber: z.number().int().positive(),
  accountId: Id,
  accountCode: z.string(),
  accountName: z.string(),
  debitRial: z.number().int().nonnegative(),
  creditRial: z.number().int().nonnegative(),
  description: z.string().optional(),
  detailId: Id.nullable().optional(),
  detailType: z.string().nullable().optional(),
  detailName: z.string().nullable().optional(),
});
export type AccountingJournalLine = z.infer<typeof AccountingJournalLine>;

export const AccountingJournalEntry = z.object({
  id: Id,
  voucherNumber: z.number().int().positive(),
  date: BusinessDate,
  description: z.string(),
  lines: z.array(AccountingJournalLine),
  totalDebitRial: z.number().int().nonnegative(),
  totalCreditRial: z.number().int().nonnegative(),
});
export type AccountingJournalEntry = z.infer<typeof AccountingJournalEntry>;

export const AccountingJournalReport = z.object({
  entries: z.array(AccountingJournalEntry),
  totalDebitRial: z.number().int().nonnegative(),
  totalCreditRial: z.number().int().nonnegative(),
  voucherCount: z.number().int().nonnegative(),
  fromDate: BusinessDate.optional(),
  toDate: BusinessDate.optional(),
});
export type AccountingJournalReport = z.infer<typeof AccountingJournalReport>;

export const AccountingLedgerEntry = z.object({
  voucherId: Id,
  voucherNumber: z.number().int().positive(),
  date: BusinessDate,
  description: z.string(),
  debitRial: z.number().int().nonnegative(),
  creditRial: z.number().int().nonnegative(),
  balanceDebitRial: z.number().int().nonnegative(),
  balanceCreditRial: z.number().int().nonnegative(),
  detailName: z.string().nullable().optional(),
});
export type AccountingLedgerEntry = z.infer<typeof AccountingLedgerEntry>;

export const AccountingLedgerReport = z.object({
  account: Account,
  openingBalanceDebitRial: z.number().int().nonnegative(),
  openingBalanceCreditRial: z.number().int().nonnegative(),
  entries: z.array(AccountingLedgerEntry),
  totalTurnDebitRial: z.number().int().nonnegative(),
  totalTurnCreditRial: z.number().int().nonnegative(),
  closingBalanceDebitRial: z.number().int().nonnegative(),
  closingBalanceCreditRial: z.number().int().nonnegative(),
  fromDate: BusinessDate.optional(),
  toDate: BusinessDate.optional(),
});
export type AccountingLedgerReport = z.infer<typeof AccountingLedgerReport>;

export const AccountingTrialBalanceRow = z.object({
  account: Account,
  openingBalanceDebitRial: z.number().int().nonnegative(),
  openingBalanceCreditRial: z.number().int().nonnegative(),
  turnDebitRial: z.number().int().nonnegative(),
  turnCreditRial: z.number().int().nonnegative(),
  closingBalanceDebitRial: z.number().int().nonnegative(),
  closingBalanceCreditRial: z.number().int().nonnegative(),
});
export type AccountingTrialBalanceRow = z.infer<typeof AccountingTrialBalanceRow>;

export const AccountingTrialBalanceReport = z.object({
  rows: z.array(AccountingTrialBalanceRow),
  level: AccountLevel.optional(),
  totalOpeningDebitRial: z.number().int().nonnegative(),
  totalOpeningCreditRial: z.number().int().nonnegative(),
  totalTurnDebitRial: z.number().int().nonnegative(),
  totalTurnCreditRial: z.number().int().nonnegative(),
  totalClosingDebitRial: z.number().int().nonnegative(),
  totalClosingCreditRial: z.number().int().nonnegative(),
  isBalanced: z.boolean(),
  fromDate: BusinessDate.optional(),
  toDate: BusinessDate.optional(),
});
export type AccountingTrialBalanceReport = z.infer<typeof AccountingTrialBalanceReport>;

export const AccountingDetailTurnoverRow = z.object({
  detailId: Id,
  organizationId: Id,
  organizationName: z.string(),
  openingBalanceDebitRial: z.number().int().nonnegative(),
  openingBalanceCreditRial: z.number().int().nonnegative(),
  turnDebitRial: z.number().int().nonnegative(),
  turnCreditRial: z.number().int().nonnegative(),
  closingBalanceDebitRial: z.number().int().nonnegative(),
  closingBalanceCreditRial: z.number().int().nonnegative(),
});
export type AccountingDetailTurnoverRow = z.infer<typeof AccountingDetailTurnoverRow>;

export const AccountingDetailTurnoverEntry = z.object({
  voucherId: Id,
  voucherNumber: z.number().int().positive(),
  date: BusinessDate,
  description: z.string(),
  accountCode: z.string(),
  accountName: z.string(),
  debitRial: z.number().int().nonnegative(),
  creditRial: z.number().int().nonnegative(),
  balanceDebitRial: z.number().int().nonnegative(),
  balanceCreditRial: z.number().int().nonnegative(),
});
export type AccountingDetailTurnoverEntry = z.infer<typeof AccountingDetailTurnoverEntry>;

export const AccountingDetailTurnoverReport = z.object({
  rows: z.array(AccountingDetailTurnoverRow),
  entries: z.array(AccountingDetailTurnoverEntry).optional(),
  totalOpeningDebitRial: z.number().int().nonnegative(),
  totalOpeningCreditRial: z.number().int().nonnegative(),
  totalTurnDebitRial: z.number().int().nonnegative(),
  totalTurnCreditRial: z.number().int().nonnegative(),
  totalClosingDebitRial: z.number().int().nonnegative(),
  totalClosingCreditRial: z.number().int().nonnegative(),
  fromDate: BusinessDate.optional(),
  toDate: BusinessDate.optional(),
  organizationId: Id.optional(),
});
export type AccountingDetailTurnoverReport = z.infer<typeof AccountingDetailTurnoverReport>;

// ==========================================
// FISCAL YEARS & PERIOD LOCK & YEAR CLOSE (AG-43)
// ==========================================

export const FiscalYearStatus = z.enum(["open", "closed"]);
export type FiscalYearStatus = z.infer<typeof FiscalYearStatus>;

export const FiscalYear = z.object({
  id: Id,
  version: Version.optional(),
  year: z.number().int().positive(),
  title: z.string().min(1),
  startDate: BusinessDate,
  endDate: BusinessDate,
  status: FiscalYearStatus.default("open"),
  closedAt: Instant.nullable().optional(),
  closedBy: Id.nullable().optional(),
  closingVoucherId: Id.nullable().optional(),
  balanceClosingVoucherId: Id.nullable().optional(),
  nextOpeningVoucherId: Id.nullable().optional(),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
});
export type FiscalYear = z.infer<typeof FiscalYear>;

export const FiscalYearCreateInput = z.object({
  year: z.number().int().positive(),
  title: z.string().min(1),
  startDate: BusinessDate,
  endDate: BusinessDate,
});
export type FiscalYearCreateInput = z.infer<typeof FiscalYearCreateInput>;

export const PeriodLock = z.object({
  id: Id,
  version: Version.optional(),
  fiscalYear: z.number().int().positive(),
  periodNumber: z.number().int().min(1).max(12),
  title: z.string().min(1),
  fromDate: BusinessDate,
  toDate: BusinessDate,
  isLocked: z.boolean(),
  lockedAt: Instant.nullable().optional(),
  lockedBy: Id.nullable().optional(),
  unlockedAt: Instant.nullable().optional(),
  unlockedBy: Id.nullable().optional(),
  unlockReason: z.string().nullable().optional(),
});
export type PeriodLock = z.infer<typeof PeriodLock>;

export const LockPeriodInput = z.object({
  fiscalYear: z.number().int().positive(),
  periodNumber: z.number().int().min(1).max(12),
});
export type LockPeriodInput = z.infer<typeof LockPeriodInput>;

export const UnlockPeriodInput = z.object({
  fiscalYear: z.number().int().positive(),
  periodNumber: z.number().int().min(1).max(12),
  reason: z.string().min(1),
});
export type UnlockPeriodInput = z.infer<typeof UnlockPeriodInput>;

export const YearCloseAccountSummary = z.object({
  account: Account,
  turnDebitRial: z.number().int().nonnegative(),
  turnCreditRial: z.number().int().nonnegative(),
  balanceDebitRial: z.number().int().nonnegative(),
  balanceCreditRial: z.number().int().nonnegative(),
});
export type YearCloseAccountSummary = z.infer<typeof YearCloseAccountSummary>;

export const YearClosePreview = z.object({
  fiscalYear: z.number().int().positive(),
  temporaryAccounts: z.array(YearCloseAccountSummary),
  permanentAccounts: z.array(YearCloseAccountSummary),
  totalRevenueRial: z.number().int().nonnegative(),
  totalExpenseRial: z.number().int().nonnegative(),
  netProfitLossRial: z.number().int(),
  resultAccount: Account,
  canClose: z.boolean(),
  blockingReasons: z.array(z.string()).default([]),
});
export type YearClosePreview = z.infer<typeof YearClosePreview>;

export const CloseFiscalYearInput = z.object({
  fiscalYear: z.number().int().positive(),
  closingDate: BusinessDate,
  nextYearOpeningDate: BusinessDate,
});
export type CloseFiscalYearInput = z.infer<typeof CloseFiscalYearInput>;

export const YearCloseResult = z.object({
  fiscalYear: z.number().int().positive(),
  temporaryClosingVoucherId: Id,
  permanentClosingVoucherId: Id,
  nextYearOpeningVoucherId: Id,
});
export type YearCloseResult = z.infer<typeof YearCloseResult>;

export const ReopenFiscalYearInput = z.object({
  fiscalYear: z.number().int().positive(),
  reason: z.string().min(1),
});
export type ReopenFiscalYearInput = z.infer<typeof ReopenFiscalYearInput>;
